import React, { createContext, useContext, useState, useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import { jwtDecode } from 'jwt-decode';
import { login, loginDirect, logout, getUser, handleResetPassword, handleConfirmResetPassword, handleConfirmSignIn, handleUpdateUserAttribute } from './amplifySetup';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkUser = async () => {
        try {
            const data = await getUser();
            if (data) {
                const accessToken = data.token;
                const idToken = data.idToken;

                // Set token immediately for API calls
                localStorage.setItem('accessToken', accessToken);

                // Decode tokens
                let groups = [];
                let decodedAttributes = {};
                try {
                    const decodedAccess = jwtDecode(accessToken);
                    groups = decodedAccess['cognito:groups'] || [];

                    if (idToken) {
                        const decodedId = jwtDecode(idToken);
                        decodedAttributes = decodedId;
                        if (groups.length === 0 && decodedId['cognito:groups']) {
                            groups = decodedId['cognito:groups'];
                        }
                    } else {
                        decodedAttributes = decodedAccess;
                    }
                } catch (e) {
                    console.error("Failed to decode token", e);
                }

                // Initial user object
                let finalUser = {
                    ...data.user,
                    attributes: { ...decodedAttributes, ...data.user },
                    roles: groups,
                    accessToken: accessToken
                };

                // Remove fallback to userService.getAllUsers()
                // Trust the tokens for name, email, and roles.
                // If the name is a UUID, we just use it as is, or use email as fallback.
                if (!finalUser.attributes.name) {
                    finalUser.attributes.name = finalUser.attributes.email || finalUser.username;
                }

                // Extract Division from groups if not present in attributes
                if (!finalUser.attributes.division) {
                    let inferredDivision = "UNKNOWN";
                    if (groups.includes("DG") || groups.includes("DG_OFFICE")) {
                        inferredDivision = "ALL";
                    } else {
                        for (let g of groups) {
                            if (g.startsWith("COORDINATOR_") || g.startsWith("EMPLOYEE_")) {
                                const parts = g.split("_");
                                if (parts.length >= 2) {
                                    inferredDivision = parts.slice(1).join("_");
                                    break;
                                }
                            }
                        }
                    }
                    finalUser.attributes.division = inferredDivision;
                }

                setUser(finalUser);
                setToken(accessToken);
            } else {
                setUser(null);
                setToken(null);
                localStorage.removeItem('accessToken');
            }
        } catch (err) {
            console.error("CheckUser failed", err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial check
        checkUser();

        // Listen for auth events (Amplify Hub)
        const listener = Hub.listen('auth', ({ payload: { event } }) => {
            switch (event) {
                case 'signIn':
                case 'tokenRefresh':
                    checkUser();
                    break;
                case 'signOut':
                    setUser(null);
                    setToken(null);
                    localStorage.removeItem('accessToken');
                    setLoading(false);
                    break;
                case 'signInWithRedirect':
                    break;
                case 'signIn_failure':
                    setLoading(false);
                    break;
                default:
                    break;
            }
        });

        return () => listener();
    }, []);

    const handleLoginDirect = async (username, password) => {
        try {
            const output = await loginDirect(username, password);
            if (output.nextStep && output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                return { success: true, nextStep: output.nextStep };
            }
            await checkUser(); // Refresh user state
            return { success: true, output };
        } catch (error) {
            console.error("Direct login failed:", error);
            const errorMessage = error?.message || (typeof error === 'string' ? error : "An unexpected error occurred.");
            return { success: false, error: errorMessage };
        }
    };

    const confirmNewPassword = async (newPassword) => {
        try {
            const output = await handleConfirmSignIn(newPassword);
            await checkUser();
            return { success: true, output };
        } catch (error) {
            console.error("Confirm sign in failed", error);
            const errorMessage = error?.message || (typeof error === 'string' ? error : "An unexpected error occurred.");
            return { success: false, error: errorMessage };
        }
    };

    const updateUserAttribute = async (key, value) => {
        try {
            await handleUpdateUserAttribute(key, value);
            await checkUser(); // Refresh user state
            return { success: true };
        } catch (error) {
            console.error("Update user attribute failed", error);
            const errorMessage = error?.message || (typeof error === 'string' ? error : "An unexpected error occurred.");
            return { success: false, error: errorMessage };
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            login,
            loginDirect: handleLoginDirect,
            confirmNewPassword,
            resetPassword: handleResetPassword,
            confirmResetPassword: handleConfirmResetPassword,
            updateUserAttribute,
            logout,
            loading,
            isAuthenticated: !!user
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
